import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {throwError} from 'rxjs';
import {NotificationsService} from 'angular2-notifications';
import { CookieManagerService } from '../shared/Services/cookie.service';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Injectable()
export class Helper {
  public isLoading = false;

  constructor(
    public translate: TranslateService,
    private router: Router,
    private notificationService: NotificationsService,
    private cookieManager: CookieManagerService,
  ) {
  }

  public static isNumber(n) {
    return !isNaN(parseFloat(n)) && !isNaN(n - 0);
  }

  public static isEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  public static isDefined(variable: any) {
    return typeof variable !== 'undefined';
  }

  public static deepCopyObj(obj: any): any {
    return Object.assign({}, obj);
  }

  public static deepCopyArray(arr: any[]): any[] {
    return Object.assign([], arr);
  }

  public static percentageOf(number: number, percentage: number) {
    if (number === 0 || percentage === 0) {
      return 0;
    }
    // fix rounding error
    return ((number * 100) * (percentage / 100) / 100);
  }

  public static roundUp(value: number, precision = 2) {
    value = parseFloat(parseFloat(value.toString()).toFixed(12));
    const pow = Math.pow(10, precision);
    return (Math.ceil(pow * value) + Math.ceil(pow * value - Math.ceil(pow * value))) / pow;
  }

  public static bcMul(...numbersArr) {
    let res = 1;
    numbersArr.forEach((number) => {
      res = <number>number * 100 * res;
    });
    return JSON.parse(JSON.stringify(res / Math.pow(100, numbersArr.length)));
  }

  public static bcAdd(...numbersArr) {
    let res = 0;
    numbersArr.forEach((number) => {
      res += (<number>number * 100);
    });
    return JSON.parse(JSON.stringify(res / 100));
  }

  public popMessageDialog(title, messages, messageType: SweetAlertIcon = 'error', asList = true) {
    let stringMessage = messages;
    if (asList) {
      stringMessage = '<ul class="text-start">';
      if (Array.isArray(messages)) {
        stringMessage += '<li>' + messages.join('</li><li>') + '</li>';
      } else if (messages !== null && typeof messages === 'object') {
        for (const e in messages) {
          if (messages.hasOwnProperty(e)) {
            const capitalizeKey = e.charAt(0).toUpperCase() + e.substring(1);
            const v = messages[e];
            let valueHtml = '';
            if (Array.isArray(v)) {
              valueHtml = v.map(x => String(x)).join('<br>');
            } else if (v !== null && typeof v === 'object') {
              try {
                valueHtml = JSON.stringify(v);
              } catch {
                valueHtml = String(v);
              }
            } else {
              valueHtml = String(v);
            }
            stringMessage += '<li><b>' + capitalizeKey + '</b><br>' + valueHtml + '</li>';
          }
        }
      } else if (typeof messages === 'string') {
        stringMessage = messages;
      }

      if (stringMessage.includes('<ul')) {
        stringMessage += '</ul>';
      }
    }
    Swal.fire({
      icon: messageType,
      title: title,
      html: stringMessage,
      customClass: {
        confirmButton: 'btn btn-primary btn-sweet',
        htmlContainer: 'swal2-html-wrap',
        popup: 'swal2-popup-wrap'
      }
    });
  }

  public popConfirmDialog(title, messages, messageType: SweetAlertIcon = 'error', asList = true, confirmButtonText, cancelButtonText) {
    let stringMessage = messages;
    if (asList) {
      stringMessage = '<ul class="text-start">';
      if (Array.isArray(messages)) {
        stringMessage += '<li>' + messages.join('</li><li>') + '</li>';
      } else if (messages !== null && typeof messages === 'object') {
        for (const e in messages) {
          if (messages.hasOwnProperty(e)) {
            const capitalizeKey = e.charAt(0).toUpperCase() + e.substring(1);
            const v = messages[e];
            let valueHtml = '';
            if (Array.isArray(v)) {
              valueHtml = v.map(x => String(x)).join('<br>');
            } else if (v !== null && typeof v === 'object') {
              try {
                valueHtml = JSON.stringify(v);
              } catch {
                valueHtml = String(v);
              }
            } else {
              valueHtml = String(v);
            }
            stringMessage += '<li><b>' + capitalizeKey + '</b><br>' + valueHtml + '</li>';
          }
        }
      } else if (typeof messages === 'string') {
        stringMessage = messages;
      }

      if (stringMessage.includes('<ul')) {
        stringMessage += '</ul>';
      }
    }
    return Swal.fire({
      icon: messageType,
      title: title,
      html: stringMessage,
      customClass: {
        confirmButton: 'btn btn-primary btn-sweet',
        cancelButton: 'mb-0',
        htmlContainer: 'swal2-html-wrap',
        popup: 'swal2-popup-wrap'
      },
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText
    });
  }

  openSuccessCancelSwal() {
  }

  public catchError(response) {
    let body: any = response.error;

    if (response.error instanceof ArrayBuffer) {
      body = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(response.error)));
    }

    if (response.status === 500) {
      this.popMessageDialog(this.translate.instant('errors.500_title'),
        '<p>' + this.translate.instant('errors.500_content_line1') + '</p>' +
        '<p style="margin-top: 10px">' + this.translate.instant('errors.500_content_line2') + '</p>' +
        '<p>' + this.translate.instant('errors.500_content_line3') + '</p>');
    } else if (response.status === 401) {
      this.cookieManager.deleteAllCookies();
      const path = window.location.pathname;
      if (path !== '/login') {
        return this.router.navigateByUrl('/login');
      }
    } else if (body && body.errors !== undefined) {
      this.popMessageDialog(body.title, body.errors);
    } else {
      this.popMessageDialog(this.translate.instant('global.connection_error_title'),
        this.translate.instant('global.connection_error_content'), 'error', false);
    }

    return throwError(() => (body && body.errors !== undefined ? body.errors : response));
  }

  public fileLinkDialog(title, content) {
    Swal.fire({
      icon: 'success',
      title: title,
      html: content
    });
  }

  public handleError(error) {
    if (error instanceof HttpErrorResponse) {
      console.error(error);
    }
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.title}`);
    }
    // return an observable with a user-facing error message
    return throwError(
      'Something bad happened; please try again later.');
  }

  showNotify(title, content, type, temp = null) {
    return this.notificationService.create(title, content, type, temp);
  }

  /*
  * @param resp Response as arraybuffer with observe 'response'
  */
  fileDownloadMap(resp: HttpResponse<ArrayBuffer>, onlyShow = false) {
    let result = null;
    if (resp.headers.has('content-disposition')) {
      const contentDispositionHeader = resp.headers.get('content-disposition');
      const results = contentDispositionHeader.split(';');
      result = results[1].trim().split('=')[1];
      if (results.length > 2) {
        result = decodeURIComponent(results[2].trim().split('=')[1]).replace('utf-8\'\'', '');
      }
    }
    if (!resp.headers.has('content-type')) {
      throw new Error('Content type not set!');
    }
    const type = resp.headers.get('content-type');
    // @ts-ignore
    const response = {
      url: window.URL.createObjectURL(new Blob([resp.body], {type})),
      filename: result ? result.replace(/"/g, '') : null
    };
    if (!onlyShow) {
      // create hidden dom element (so it works in all browsers)
      const a = document.createElement('a');
      a.setAttribute('style', 'display:none;');
      a.setAttribute('target', '_blank');
      document.body.appendChild(a);
      // create file, attach to hidden element and open hidden element
      a.href = response.url;
      if (response.filename) {
        a.download = response.filename;
      }
      a.click();
      document.body.removeChild(a);
    }
    return response;
  }

  public createDownloadLink(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  public addHideRecaptchaBadgeClassToBody() {
    document.body.classList.add("hide-recaptcha-badge");
  }

  public removeHideRecaptchaBadgeClassToBody() {
    document.body.classList.remove("hide-recaptcha-badge");
  }
}




